import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, ArrowRight, ArrowLeft, Award, FileQuestion, RotateCw } from "lucide-react";
import { Button } from "./ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuizModal({ enrollmentId, onClose, onPassed }) {
  const [domande, setDomande] = useState([]);
  const [risposte, setRisposte] = useState({});
  const [step, setStep] = useState(0); // index domanda corrente o "submit" or "result"
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    axios.get(`${API}/api/me/corsi/${enrollmentId}/quiz`, { withCredentials: true })
      .then((r) => setDomande(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Errore caricamento quiz"))
      .finally(() => setLoading(false));
    return () => { document.body.style.overflow = ""; };
  }, [enrollmentId]);

  const isLast = step === domande.length - 1;
  const corrente = domande[step];
  const tutteRisposte = domande.every((_, i) => risposte[i] !== undefined);

  const handleSelect = (idx) => setRisposte({ ...risposte, [step]: idx });

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const risposteArr = domande.map((_, i) => risposte[i] ?? -1);
      const { data } = await axios.post(
        `${API}/api/me/corsi/${enrollmentId}/quiz/submit`,
        { risposte: risposteArr },
        { withCredentials: true }
      );
      setResult(data);
      if (data.superato) onPassed?.(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore invio quiz");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestart = () => {
    setResult(null);
    setRisposte({});
    setStep(0);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-2xl my-8 shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#102a6b] to-[#1e3a8a] text-white p-6 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-white/15 flex items-center justify-center">
            <FileQuestion className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-extrabold text-[#fbbf24]">Quiz finale</div>
            <h2 className="text-xl font-extrabold">Verifica delle conoscenze</h2>
          </div>
          <button onClick={onClose} data-testid="quiz-close" className="h-9 w-9 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center text-xl">×</button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#102a6b]" /></div>
          ) : error && !result ? (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-4 text-[13px] font-semibold">{error}</div>
          ) : result ? (
            <ResultView result={result} onClose={onClose} onRestart={handleRestart} />
          ) : domande.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nessuna domanda disponibile</p>
          ) : (
            <>
              {/* Stepper */}
              <div className="flex items-center gap-1.5 mb-5">
                {domande.map((_, i) => (
                  <div
                    key={i}
                    className={`flex-1 h-1.5 rounded-full ${
                      i < step ? "bg-emerald-500" :
                      i === step ? "bg-[#f59e0b]" :
                      "bg-slate-200"
                    }`}
                  />
                ))}
              </div>
              <div className="text-[11px] uppercase tracking-wider font-extrabold text-slate-500 mb-2">
                Domanda {step + 1} di {domande.length}
              </div>
              <h3 className="text-lg font-extrabold text-[#0a1f55] leading-tight">{corrente.testo}</h3>

              <div className="mt-5 space-y-2.5">
                {corrente.risposte.map((r, i) => {
                  const selected = risposte[step] === i;
                  return (
                    <button
                      key={i}
                      data-testid={`quiz-answer-${i}`}
                      onClick={() => handleSelect(i)}
                      className={`w-full text-left p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                        selected ? "border-[#102a6b] bg-[#102a6b]/5" : "border-slate-200 hover:border-[#102a6b]/40"
                      }`}
                    >
                      <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center text-[11px] font-extrabold ${
                        selected ? "border-[#102a6b] bg-[#102a6b] text-white" : "border-slate-300 text-slate-500"
                      }`}>
                        {String.fromCharCode(65 + i)}
                      </div>
                      <span className="flex-1 text-[14px] text-[#0a1f55] font-semibold">{r}</span>
                      {selected && <CheckCircle2 className="h-4 w-4 text-[#102a6b]" />}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex gap-2 justify-between">
                <Button
                  variant="outline"
                  onClick={() => setStep(Math.max(0, step - 1))}
                  disabled={step === 0}
                  className="border-slate-300"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Indietro
                </Button>
                {isLast ? (
                  <Button
                    data-testid="quiz-submit"
                    onClick={handleSubmit}
                    disabled={!tutteRisposte || submitting}
                    className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Invia risposte <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                ) : (
                  <Button
                    data-testid="quiz-next"
                    onClick={() => setStep(step + 1)}
                    disabled={risposte[step] === undefined}
                    className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold"
                  >
                    Avanti <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultView({ result, onClose, onRestart }) {
  const passed = result.superato;
  return (
    <div className="text-center py-4">
      <div className={`mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-5 ${
        passed ? "bg-emerald-500 text-white shadow-xl shadow-emerald-500/30" : "bg-red-500 text-white"
      }`}>
        {passed ? <CheckCircle2 className="h-10 w-10" /> : <XCircle className="h-10 w-10" />}
      </div>
      <h3 className="text-2xl font-extrabold text-[#0a1f55]">{passed ? "Quiz superato!" : "Quiz non superato"}</h3>
      <div className="mt-3 text-[14px] text-slate-600">
        Hai risposto correttamente a <strong className="text-[#0a1f55]">{result.corrette}/{result.totale}</strong> domande
      </div>
      <div className="mt-1 text-3xl font-extrabold text-[#102a6b]">{result.punteggio}%</div>
      <div className="text-[11.5px] text-slate-500">Soglia minima: {result.soglia}%</div>

      {passed && result.cfdt_awarded > 0 && (
        <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#f59e0b]/15 text-[#b45309] text-[13px] font-extrabold">
          <Award className="h-4 w-4" /> +{result.cfdt_awarded} CFDT accreditati • Attestato sbloccato
        </div>
      )}

      <div className="mt-7 flex gap-2 justify-center">
        {passed ? (
          <Button onClick={onClose} className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
            Chiudi
          </Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose} className="border-slate-300">Esci</Button>
            <Button onClick={onRestart} data-testid="quiz-retry" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold">
              <RotateCw className="mr-2 h-4 w-4" /> Riprova
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
